import React from "react";
import "../common/LicenseDetail.css";
import { useState, useEffect,useRef } from "react";
import Icon from "../../../common/Icon";
import downloadButton from "./../../../../assets/img/icon/downloadButton.svg";
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import { Storage } from "../../../../backend/storage";
import { hasUserAccess, getUserDetails } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import EditDetails from "../common/EditDetails";
import PageLoader from "../../../shared/PageLoader";
import CostDetailsAccordion from "../common/CostDetailsAccordion";
import { CostEditFormData } from "../common/CostDetailsEditForm";
import {
  useUpdateCompanyLicense,
  useUpdateCostDetail,
  useGetApplicationTypes,
  useGetCompanyLicenseDocuments,
  useGetCompanyLicenseApplications,
  useGetCompanyLicenseHistory,
  useCreateLicenseApplication,
  useGetCompanyLicense,
} from "../../../../backend/license";
import { useGetStates, useGetCities } from "../../../../backend/masters";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { downloadS3FileSecurely, formatDateDisplay } from "../../../../utils/common";
const USER_ROLE = "user-role";

interface LicenseDetailProps {
  isOpen: boolean;
  license?: any;
  closePanel: () => void;
  refetch?: () => void;
}
interface HistoryChange {
  from: any;
  to: any;
}

interface HistoryItem {
  id: number;
  action: string;
  entity_type?: string;
  entity_info?: any;
  changed_by: string;
  changed_at: string;
  changes: Record<string, HistoryChange>;
}

interface DisplayHistoryItem {
  id: number;
  title: string;
  date: string;
  rawDate: string;
  description: string;
  name: string;
}

type PopupData = {
  title: string;
  description: string;
  confirmation: string;
};

interface DocumentItem {
  id: number;
  label: string;
  fileName: string;
  fileUrl: string;
}

// Record-level permission checks
export function canEditLicenseApplication(application: any) {
  const user = getUserDetails();
  const userRole = Storage.getValue(USER_ROLE);

  // Check privilege first
  if (!hasUserAccess(USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION)) {
    return false;
  }

  // License Owner: check record-level assignment
  if (userRole === "License Owner") {
    return (
      application.editor_ids && application.editor_ids.includes(user.user_id)
    );
  }

  // License Manager: can edit any
  return true;
}

export function canEditCompanyLicenseCostDetails(license: any) {
  const user = getUserDetails();
  const userRole = Storage.getValue(USER_ROLE);

  // Check privilege first - users with EDIT_COST_DETAILS privilege can edit
  if (hasUserAccess(USER_PRIVILEGES.EDIT_COST_DETAILS)) {
    return true;
  }

  // License Owner: can edit cost details if assigned to this license
  if (userRole === "License Owner") {
    const assignedToId = license.assigned_to_user_id || (license.assigned_to && license.assigned_to.id);
    return assignedToId === user.user_id;
  }

  // All other roles: cannot edit cost details
  return false;
}

export function canViewLicenseApplication(application: any) {
  const user = getUserDetails();
  const userRole = Storage.getValue(USER_ROLE);

  // License Watcher: check record-level assignment
  if (userRole === "License Watcher") {
    return (
      application.watcher_ids && application.watcher_ids.includes(user.user_id)
    );
  }

  // Others: check privilege
  return hasUserAccess(USER_PRIVILEGES.VIEW_LICENSE_APPLICATIONS);
}

const LicenseDetail: React.FC<LicenseDetailProps> = ({
  isOpen,
  closePanel,
  license: licenseProp,
  refetch,
}) => {
  // Fetch fresh license data to get updated cost_details
  const { license: fetchedLicense, isFetching: isFetchingLicense } = useGetCompanyLicense(
    licenseProp && licenseProp.id ? licenseProp.id : '',
    !!(licenseProp && licenseProp.id && isOpen)
  );

  // Use fetched license data if available, otherwise fall back to prop
  const license = (fetchedLicense && fetchedLicense.id) ? fetchedLicense : licenseProp;

  const canEdit = license ? canEditCompanyLicenseCostDetails(license) : false;
  const user = getUserDetails();
  const queryClient = useQueryClient();

  // Update cost detail mutation hook
  const { updateCostDetail, updating: updatingCostDetail } = useUpdateCostDetail(
    () => {
      if (refetch) refetch();
      queryClient.invalidateQueries(['companyLicenses']);
      queryClient.invalidateQueries(['companyLicense']);
      toast.success("Cost details updated successfully", {
        icon: <Icon name="snackbarSuccess" />,
      });
      closePanel();
    },
    (error: any) => {
      console.error("Error updating cost detail:", error);
      toast.error("Failed to update cost details");
    }
  );

  // Update mutation hook
  const { updateLicense, updating } = useUpdateCompanyLicense(
    (response: any) => {
      toast.success("License updated successfully", {
        icon: <Icon name="snackbarSuccess" />,
      });
      closeEditPopup();
      closePanel();
      queryClient.invalidateQueries(['companyLicenses']);
      queryClient.invalidateQueries(['companyLicenseDocuments']);
      queryClient.invalidateQueries(['licenseApplications']);
      if (refetch) refetch();
      setIsOpen(false);
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to update license");
      }
    }
  );

  // Get states and cities for the form
  const { states } = useGetStates({}, true);
  const [selectedStateId, setSelectedStateId] = useState<string>("");
  const { cities } = useGetCities(
    { filters: selectedStateId ? { state_id_eq: selectedStateId } : {} },
    Boolean(selectedStateId)
  );

  // Update selected state when license data changes
  useEffect(() => {
    if (license && license.state_id) {
      setSelectedStateId(license.state_id.toString());
    }
  }, [license]);

  const [activeTab, setActiveTab] = useState<
    "details" | "history" | "applications" | "documents"
  >("details");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [open, setIsOpen] = useState(false);
  const [popupData, setPopupData] = useState<PopupData | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [applicationDetailOpen, setApplicationDetailOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const DATE_ONLY_FIELDS = ["cost_period_from", "cost_period_to"];

  const openEditPopup = () => setIsEditOpen(true);
  const closeEditPopup = () => setIsEditOpen(false);

  const canShowEditButton = hasUserAccess(USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION) &&
  hasUserAccess(USER_PRIVILEGES.EDIT_COMPANY_LICENSE) && (license && license.status !== "surrendered") && (license && license.status !== "expired") && canEdit;
  
  const handleClosePanel = () => {
    setActiveTab("details");
    closePanel();
  };

  const getActionLabel = (action: "up_for_renewal" | "amendment" | "surrendered" | null) => {
    if (!action) return "Application";
    if (action === "up_for_renewal") return "Renewal application";
    if (action === "amendment") return "Amendment application";
    if (action === "surrendered") return "Surrender application";
    return "Application";
  };

  // Helper function to get action type badge class
  const getActionTypeBadgeClass = (actionType: string) => {
    switch (actionType) {
      case "renewal":
        return "renewal";
      case "amendment":
        return "renewal";
      case "surrender":
        return "expired";
      case "new":
        return "active";
      default:
        return "not_started";
    }
  };
  
  const { createApplication, creating: creatingApplication } =
    useCreateLicenseApplication(
      (response: any) => {
        const actionLabel = getActionLabel(pendingActionRef.current);
        toast.success(`${actionLabel} created successfully`, {
           icon: <Icon name="snackbarSuccess" />,
        });
        closePopup();
        // Refetch applications list to show newly created application
        if (refetchApplications) {
          refetchApplications();
        }
        // Invalidate query cache
        queryClient.invalidateQueries(['licenseApplications']);
        queryClient.invalidateQueries(['companyLicenses']);
        // Close the panel
        handleClosePanel();
        if (refetch) refetch();
      },
      (error: any) => {
        closePopup();
        handleClosePanel();
        const actionLabel = getActionLabel(pendingActionRef.current);
        const errorData = error && error.response && error.response.data;
        const errors = errorData && errorData.errors;
        const errorMessages = errorData && errorData.error_messages;

        if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach((errMsg: string) => {
            toast.error(errMsg, {
              icon: <Icon name="snackbarError" />,
            });
          });
        } else if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
          errorMessages.forEach((errMsg: string) => {
            toast.error(errMsg, {
              icon: <Icon name="snackbarError" />,
            });
          });
        } else if (errorData && errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error(`Failed to create ${actionLabel.toLowerCase()}`);
        }
      }
    );


  const formatActionType = (actionType: string) => {
    return actionType.charAt(0).toUpperCase() + actionType.slice(1);
  };

  const [pendingAction, setPendingAction] = useState<
    "up_for_renewal" | "amendment" | "surrendered" | null
  >(null);
  const pendingActionRef = useRef<"up_for_renewal" | "amendment" | "surrendered" | null>(null);
  const [hiddenButtons, setHiddenButtons] = useState<{
    renewal: boolean;
    amendment: boolean;
    surrender: boolean;
  }>({
    renewal: false,
    amendment: false,
    surrender: false,
  });

   const openPopup = (key: "up_for_renewal" | "amendment" | "surrendered") => {
    setPopupData(buttonPopups[key]);
    setPendingAction(key);
    pendingActionRef.current = key;  
    setIsOpen(true);
  };

  const { applicationTypes } = useGetApplicationTypes(
    {
      filters: {
        license_type_id_eq:
          license &&
          (license.license_type_id ||
            (license.license_type && license.license_type.id)),
      },
    },
    Boolean(
      license &&
        (license.license_type_id ||
          (license.license_type && license.license_type.id))
    )
  );

  // Check which application types are available and hide buttons accordingly
  useEffect(() => {
    if (applicationTypes && applicationTypes.length > 0) {
      const hasRenewal = applicationTypes.some((appType: any) => appType.action_type === "renewal");
      const hasAmendment = applicationTypes.some((appType: any) => appType.action_type === "amendment");
      const hasSurrender = applicationTypes.some((appType: any) => appType.action_type === "surrender");

      setHiddenButtons({
        renewal: !hasRenewal,
        amendment: !hasAmendment,
        surrender: !hasSurrender,
      });
    }
  }, [applicationTypes]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("details");
    }
  }, [isOpen]);

  // Scroll to top when tab changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  // Fetch documents for this company license
  const {
    companyLicenseDocuments,
    isFetching: loadingDocuments,
    refetch: refetchDocuments,
  } = useGetCompanyLicenseDocuments(
    license && license.id ? license.id : undefined,
    !!(license && license.id && activeTab === "documents")
  );

  // Fetch applications for this company license (exclude "new" type)
  const {
    applications,
    isFetching: loadingApplications,
    refetch: refetchApplications,
  } = useGetCompanyLicenseApplications(
    {
      filters: {
        license_reference_id_eq: license && license.id ? license.id : undefined,
      },
      include: [
        "company",
        "license_type",
        "application_type",
        "assigned_to",
        "state",
        "city"
      ],
      includes: {
        cost_details: ['invoices']
      },
      per: 100,
    },
    !!(license && license.id)
  );

  // Log fetched documents for debugging
  const isLoading = updating || creatingApplication || updatingCostDetail || isFetchingLicense;

  const handleApplicationTypeUpdate = () => {
  if (!license || !license.id || !pendingAction) {
    toast.error("License information is missing");
    closePopup();
    return;
  }

  const actionType =
   pendingAction === "amendment" ? "amendment" :
   pendingAction === "surrendered" ? "surrender" :
   "renewal";

  // Find the correct application type (buttons are already filtered, so this should always exist)
  const correctApplicationType = applicationTypes.find(
    (appType: any) => appType.action_type === actionType
  );

  if (!correctApplicationType) {
    const actionName = actionType.charAt(0) + actionType.slice(1);
    toast.error(`${actionName} Action is not configured for this type of the license. Please contact the administrator.`);
    closePopup();
    return;
  }

  const applicationPayload = {
    license_application: {
      company_id: license.company_id || license.companyid,
      associate_company_id: license.associate_company_id || license.associate_companyid,
      location_id: license.location_id,
      license_type_id: license.license_type_id || license.license_typeid,
      license_reference_id: license.id,
      authority: license.authority,
      application_type_id: correctApplicationType.id,
      assigned_to_id: license.assigned_to_user_id,
      requested_by_id: user.user_id,
      created_by_id: user.user_id,
      updated_by_id: user.user_id,
      watcher_ids: license.watcher_ids,
      status: "not_started",
      description: license.description,
      state_id: (license.license_type && license.license_type.state_id) || "",
      city_id: (license.license_type && license.license_type.city_id) || "",
      issued_date: license.issue_date,
      expiry_date: license.expiry_date,
      license_name: license.license_name,
    },
  };

  createApplication(applicationPayload as any);
};

  const closePopup = () => {
    setIsOpen(false);
    setPendingAction(null);
    pendingActionRef.current = null;
  };

  // Handle save from EditDetails modal
  const handleSave = (data: any, section: string) => {
    if (!license || !license.id) return;

    const payload = {
      id: license.id,
      company_license: {
        ...data,
        updated_by_id: user.user_id,
      },
    };

    updateLicense(payload as any);
  };

  const handleCostDetailsUpdate = async (_costTypeKey: string, data: CostEditFormData, originalData?: CostEditFormData): Promise<void> => {
    if (!license || !license.id || !data.id) return;

    // Build payload with only changed fields
    const costDetailUpdate: Record<string, any> = {
      id: data.id,
      // updated_by_id: user.user_id,
    };

    // Compare and add only changed fields
    if (!originalData || data.cost_description !== originalData.cost_description) {
      costDetailUpdate.cost_description = data.cost_description;
    }
    if (!originalData || data.cost_amount !== originalData.cost_amount) {
      costDetailUpdate.cost_amount = parseFloat(data.cost_amount) || 0;
    }
    if (!originalData || data.tax_applicable !== originalData.tax_applicable) {
      costDetailUpdate.tax_applicable = data.tax_applicable;
    }
    if (!originalData || data.tax_amount !== originalData.tax_amount) {
      costDetailUpdate.tax_amount = data.tax_applicable ? (parseFloat(data.tax_amount) || 0) : 0;
    }
    if (!originalData || data.total_cost_including_tax !== originalData.total_cost_including_tax) {
      costDetailUpdate.total_cost = parseFloat(data.total_cost_including_tax) || 0;
    }
    if (!originalData || data.vendor_service_provider_name !== originalData.vendor_service_provider_name) {
      costDetailUpdate.vendor_name = data.vendor_service_provider_name;
    }
    if (!originalData || data.payment_status !== originalData.payment_status) {
      costDetailUpdate.payment_status = data.payment_status;
    }
    if (!originalData || data.cost_period_from !== originalData.cost_period_from) {
      costDetailUpdate.cost_period_from = data.cost_period_from;
    }
    if (!originalData || data.cost_period_to !== originalData.cost_period_to) {
      costDetailUpdate.cost_period_to = data.cost_period_to;
    }

    const payload = {
      id: license.id,
      type: 'company_license',
      company_license: {
        cost_details_attributes: [costDetailUpdate],
      },
    };

    return new Promise((resolve, reject) => {
      updateCostDetail(payload as any, {
        onSuccess: () => resolve(),
        onError: (error: any) => reject(error),
      });
    });
  };

  const licenseDetails = license
    ? [
        ...(license.license_name
          ? [{ label: "License Name", value: license.license_name }]
          : []),
        { label: "Description", value: license.description || "-" },
        {
          label: "License Type",
          value: (license.license_type && license.license_type.name) || "-",
        },
        {
          label: "Company",
          value: (license.company && license.company.name) || "-",
        },
        {
          label: "Associate Company",
          value:
            (license.associate_company && license.associate_company.name) ||
            "-",
        },
        {
          label: "Location",
          value: license.location  ? `${license.location.name} - ${license.location.code}`  : "-",
        },
        {
          label: "State",
          value: (license.state && license.state.name) || "-",
        },
        {
          label: "City",
          value: (license.city && license.city.name) || "-",
        },
        { label: "Issued by", value: license.authority || "-" },
        ...(license.license_name
          ? [
              { label: "Issue Date", value: formatDateDisplay(license.issue_date) },
              {
                label: "Is Perpetual",
                value: license.is_perpetual ? "Yes" : "No",
              },
              {
                label: "Expiry Date",
                value: license.is_perpetual
                  ? "Perpetual License"
                  : formatDateDisplay(license.expiry_date),
              },
            ]
          : []),

        ...(license.assigned_to_user || license.assigned_to
          ? [
              {
                label: "Assigned To",
                value:
                  (license.assigned_to_user && license.assigned_to_user.name) ||
                  (license.assigned_to && license.assigned_to.name) ||
                  "-",
              },
            ]
          : []),
        {
          label: "Watchers",
          value:
            license.watcher_names && license.watcher_names.length > 0
              ? license.watcher_names.join(", ")
              : "No watchers",
        },
      ]
    : [];

  const formatDateTime = (isoString: string | undefined | null) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCreatedDateTime = (isoString: string | undefined | null) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour12: true,
    });
  };

  const formatIssuedDateTime = (dateString?: string | null) => {
    if (!dateString) return "-";
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    let date = isDateOnly ? new Date(dateString + "T00:00:00") : new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour12: true,
    });
  };

  const {
      historyItems,
      isFetching: isLoadingHistory,
      refetch: refetchHistory,
    } = useGetCompanyLicenseHistory(
      license && license.id ? license.id : undefined,
      "watcher_ids,created_by,updated_by,editor_ids,assigned_to_user_id,escalation_user_id,status,description,license_name",
      !!(license && license.id)
    );
    
  // Get the latest change with field updates from history API
  const formatFieldName = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const formatFieldValue = (value: any, field?: string): string => {
    if (value === null || value === undefined) return "(empty)";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string") {
      if (value.trim() === "") return "(empty)";

      const ddmmyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyyPattern.test(value)) {
        const [day, month, year] = value.split('/');
        const isoDate = `${year}-${month}-${day}`;
        return formatIssuedDateTime(isoDate);
      }

      if (field && DATE_ONLY_FIELDS.includes(field) && !isNaN(Date.parse(value))) {
        return formatIssuedDateTime(value);
      }
      const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}|$)/;
      if (isoDatePattern.test(value) && !isNaN(Date.parse(value))) {
        return formatIssuedDateTime(value);
      }
      if (value.includes('_')) {
        return value
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      }
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    if (typeof value === "object" && value.id && value.name) {
      return value.name;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return value
        .map(item =>
          typeof item === "object" && item.name ? item.name : String(item)
        )
        .join(", ");
    }
    return String(value);
  };

  const displayHistoryItems: DisplayHistoryItem[] = [];

  if (license) {
    // 1. License Created On (will be at bottom after reverse)
    const createdBy = (license.created_by && license.created_by.name) || "-";
    displayHistoryItems.push({
      id: 3,
      title: "License Created On",
      date: formatDateTime(license.created_at),
      rawDate: license.created_at,
      name: `Created By <strong style="color:#2965AD" class="text-capitalize">${createdBy}</strong>`,
      description: "",
    });

    // 2. License Issued (will be in middle after reverse)
    if (license.issue_date) {
      const issuedBy = (license.created_by && license.created_by.name) || "-";

      displayHistoryItems.push({
        id: 1,
        title: "License Issued",
        date: formatIssuedDateTime(license.issue_date),
        rawDate: license.issue_date,
        name: `Action taken by <strong style="color:#2965AD" class="text-capitalize">${issuedBy}</strong>`,
        description: "",
      });
    }

    // 3. License Edited - Group cost detail changes in one box, separate entries for license changes
    const editItems: DisplayHistoryItem[] = [];
    if (historyItems && historyItems.length > 0) {
      historyItems.forEach((historyItem: HistoryItem, historyIndex: number) => {
        if (historyItem.changes && Object.keys(historyItem.changes).length > 0) {
          // Check if this is a cost_detail edit
          const isCostDetail = historyItem.entity_type === "cost_detail";

          if (isCostDetail) {
            // Group all cost detail changes in one history item
            const changesList = Object.keys(historyItem.changes).map((field: string) => {
              const change = historyItem.changes[field];
              const fieldLabel = formatFieldName(field);
              const fromValue = formatFieldValue(change.from, field);
              const toValue = formatFieldValue(change.to, field);
              return `${fieldLabel} changed from <strong>${fromValue}</strong> to <strong>${toValue}</strong>`;
            }).join('<br/>');

            const categoryInfo = historyItem.entity_info && historyItem.entity_info.cost_category
              ? ` (${historyItem.entity_info.cost_category})`
              : '';

            editItems.push({
              id: 1000 + historyIndex * 100,
              title: "Cost Details Edited" + categoryInfo,
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `${changesList} by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || "-"}</strong>`,
              description: "",
            });
          } else {
            Object.keys(historyItem.changes).forEach((field: string, fieldIndex: number) => {
              const change = historyItem.changes[field];
              const fieldLabel = formatFieldName(field);
              const fromValue = formatFieldValue(change.from, field);
              const toValue = formatFieldValue(change.to, field);

              editItems.push({
                id: 1000 + historyIndex * 100 + fieldIndex,
                title: "License Edited",
                date: formatDateTime(historyItem.changed_at),
                rawDate: historyItem.changed_at,
                name: `${fieldLabel} changed from <strong>${fromValue}</strong> to <strong>${toValue}</strong> by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || "-"}</strong>`,
                description: "",
              });
            });
          }
        }
      });
    }

    editItems.sort((a, b) => {
      const dateA = new Date(a.rawDate || 0).getTime();
      const dateB = new Date(b.rawDate || 0).getTime();
      return dateA - dateB;
    });

    displayHistoryItems.push(...editItems);
  }

  displayHistoryItems.reverse();

  // Map fetched documents to display format
  const documentList: DocumentItem[] = companyLicenseDocuments.map(
    (doc: any) => ({
      id: doc.id,
      label: doc.document_type ? doc.document_type.name : "Unknown Document",
      fileName: doc.asset ? doc.asset.file_name : "No file",
      fileUrl: doc.asset ? doc.asset.file_path : "",
    })
  );

  let uploadProgress = documentList.length > 0 ? 100 : 0;

  useEffect(() => {
    if (activeTab !== "documents") return;

    let start = 0;
    const duration = 1800;
    const increment = uploadProgress / (duration / 16);
    let animationFrame: number;

    const animate = () => {
      start += increment;
      if (start < uploadProgress) {
        setAnimatedProgress(Math.floor(start));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setAnimatedProgress(uploadProgress);
        cancelAnimationFrame(animationFrame);
      }
    };

    setAnimatedProgress(0);
    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [activeTab, uploadProgress]);

  const buttonPopups: { [key: string]: PopupData } = {
    up_for_renewal: {
      title: "Apply for renewal",
      description:
        "This action will create a new application for the selected license action.",
      confirmation:
        "Are you sure you want to create an application for renewal?",
    },
    amendment: {
      title: "Apply for amendment",
      description: "Apply for amendment",
      confirmation:
        "Are you sure you want to create an application for amendment?",
    },
    surrendered: {
      title: "Apply for surrender",
      description:
        "This action will create a new surrender application for the selected license action.",
      confirmation:
        "Are you sure you want to create an application for surrender?",
    },
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    if (!fileUrl) {
      toast.error("File URL not available");
      return;
    }
    // Download file securely using S3 presigned URL
    try {
      await downloadS3FileSecurely(fileName, fileUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  return (
    <>
      {isOpen && (
        <div className="licensedetails-overlay" onClick={handleClosePanel}></div>
      )}
      <div className="licensedetails-wrapper">
        <div className={`licensedetails ${isOpen ? "open" : ""}`} style={{height: "calc(100% - 151px)"}} ref={scrollContainerRef}>
          <div className="licensedetails-header pb-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-semibold">License Details</h5>
              <div className="d-flex justify-content-between align-items-center gap-4">
                {canShowEditButton && (
                  <div
                    className="d-flex align-items-center gap-2 cursor-pointer"
                    onClick={openEditPopup}
                  >
                    <img src={editIcon} alt="editIcon" />
                    <span className="edit_button">Edit</span>
                  </div>
                )}
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClosePanel}
                ></button>
              </div>
            </div>

            <div>
              <ul className="progress-section p-0 m-0 mt-4 pl-2">
                <li
                  className={activeTab === "details" ? "active" : ""}
                  onClick={() => setActiveTab("details")}
                >
                  Details
                </li>
                <li
                  className={activeTab === "documents" ? "active" : ""}
                  onClick={() => setActiveTab("documents")}
                >
                  Documents
                </li>
                <li
                  className={activeTab === "history" ? "active" : ""}
                  onClick={() => setActiveTab("history")}
                >
                  History
                </li>
                {applications && applications.length > 0 && (
                  <li
                    className={activeTab === "applications" ? "active" : ""}
                    onClick={() => setActiveTab("applications")}
                  >
                    Applications
                  </li>
                )}
              </ul>
            </div>
          </div>
          
          <div className="licensedetails-body h-100">
            {/* License Details Tab */}
            {activeTab === "details" && (
              <>
                <ul className="licensedetails-text pb-1">
                  {licenseDetails.map((item, index) => (
                    <li key={index}>
                      <div>{item.label}</div>
                      <div style={item.label === "Description" ? { whiteSpace: 'pre-wrap' } : undefined}>{item.value}</div>
                    </li>
                  ))}
                </ul>

                {/* Cost Details Accordion */}
                {license && license.cost_details && license.cost_details.length > 0 && (
                  <CostDetailsAccordion
                    costDetails={license.cost_details}
                    licenseId={license.id}
                    onUpdate={handleCostDetailsUpdate}
                    canEdit={canEdit}
                    isLoading={updating}
                  />
                )}
              </>
            )}

            {activeTab === "documents" && (
              <>
                <div className="document-container h-100 pt-4">
                  {companyLicenseDocuments &&
                  companyLicenseDocuments.length > 0 ? (
                    <div className="document-list">
                      {companyLicenseDocuments.map((doc: any) => {
                        var documentName = doc.is_issued_document
                          ? "Final License Document"
                          : (doc.application_type_document &&
                              doc.application_type_document.document_type &&
                              doc.application_type_document.document_type
                                .name) ||
                            "Unknown Document";

                        var fileName =
                          (doc.asset && doc.asset.original_filename) ||
                          (doc.asset && doc.asset.name) ||
                          "No file";
                        var fileUrl = (doc.asset && doc.asset.file_path) || "";

                        return (
                          <div key={doc.id} className="document-item mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <label className="form-label w-50 ellipse">
                                {documentName}
                              </label>
                              <div className="upload-successful position-relative">
                              <div className="p-4 d-flex align-items-center justify-content-between">
                                <p className="text-sm fw-semibold m-0 img_name_truncate">
                                  {fileName}
                                </p>
                                <div
                                  style={{
                                    cursor: "pointer",
                                    height: "25px",
                                    width: "25px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(fileUrl, fileName);
                                  }}
                                >
                                  <img
                                    className="d-block"
                                    src={downloadButton}
                                    alt="downloadButton"
                                  />
                                </div>
                              </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted h-100 w-100 d-flex justify-content-center align-items-center">
                      No documents uploaded for this license.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* History Tab */}
             {activeTab === "history" && (
              <>
                <div className="history-accordion">
                  {displayHistoryItems.map((item, index) => (
                    <div key={item.id} className="accordion-item">
                      <div className="accordion-timeline">
                        <div
                          className={`timeline-dot d-flex justify-content-center align-items-center ${
                            item ? "active" : ""
                          }`}
                        >
                          <div className="progress-checked"></div>{" "}
                          {/* add class "completed" when need to show checked */}
                        </div>
                        {index < displayHistoryItems.length - 1 && (
                          <div className="timeline-line">
                            <div
                              className={`dark-line ${
                                expandedId === item.id ? "expanded" : ""
                              }`}
                            ></div>
                          </div>
                        )}
                      </div>
                      <div className="accordion-content-wrapper">
                        <div
                          className="accordion-header"
                        >
                          <div className="accordion-header-left">
                            <h6
                              className="accordion-title mb-0"
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            ></h6>
                           <span className="accordion-name" dangerouslySetInnerHTML={{ __html: item.name }}></span>
                            <span className="accordion-date">{item.date}</span>
                          </div>
                        </div>
                        <div
                          className={`accordion-body ${
                            expandedId === item.id ? "expanded" : "collapsed"
                          }`}
                        >
                          <div className="accordion-body-content">
                            {item.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Applications Tab */}
            {activeTab === "applications" && (
              <div className="mt-4">
                {loadingApplications ? (
                  <div className="text-center py-4">
                    <p className="text-muted">Loading applications...</p>
                  </div>
                ) : applications && applications.length > 0 ? (
                  applications.map((app: any) => (
                    <div key={app.id} className="accordion-item pb-3 mb-0">
                      <div className="accordion-content-wrapper">
                        <div
                          className="accordion-header cursor-pointer"
                          onClick={() => {
                            setSelectedApplication(app);
                            setApplicationDetailOpen(true);
                          }}
                        >
                          <div className="accordion-header-left">
                            <div className="d-flex align-items-center gap-2">
                              <h6 className="accordion-title mb-0">
                                {app.license_type && app.license_type.name
                                  ? app.license_type.name
                                  : "License Application"}
                              </h6>
                              {app.application_type && app.application_type.action_type && (
                                <span className={`license-tag text-sm fw-bold ${getActionTypeBadgeClass(app.application_type.action_type)} color-class`}>
                                  {formatActionType(app.application_type.action_type)}
                                </span>
                              )}
                            </div>
                            <div className="d-flex flex-column gap-1">
                              <span className="accordion-name">
                                {app && app.company_name
                                  ? app.company_name
                                  : "-"}
                              </span>
                              <span className="accordion-date">
                                Created On: {formatCreatedDateTime(app.created_at)}
                              </span>
                              <span className="accordion-date">
                                Status: {formatFieldName(app.status)}
                              </span>
                              <span className="accordion-date">
                                Updated On: {formatIssuedDateTime(app.updated_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      No applications found for this license.
                    </p>
                    <p className="text-muted text-sm">
                      Applications for renewal, amendment, or surrender will
                      appear here.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {license &&
            activeTab === "details" &&
            license.status !== "surrendered" && license.status !== "expired" &&
            hasUserAccess(USER_PRIVILEGES.EDIT_COMPANY_LICENSE) && (
              <div className="d-flex gap-3 w-100">
                {!license.is_perpetual && !hiddenButtons.renewal && (
                  <button
                    type="button"
                    className="button-primary w-100 fw-semibold"
                    onClick={() => openPopup("up_for_renewal")}
                  >
                    <Icon name={"recyle"} className="pr-2"></Icon>
                    Apply for Renewal
                  </button>
                )}

                {!hiddenButtons.amendment && (
                  <button
                    type="button"
                    className="button-secondary w-100 fw-semibold"
                    onClick={() => openPopup("amendment")}
                  >
                    <Icon name={"amendment"} className="pr-2"></Icon>
                    Apply for Amendment
                  </button>
                )}

                {!hiddenButtons.surrender && (
                  <button
                    type="button"
                    className="button-warning w-100 fw-semibold"
                    onClick={() => openPopup("surrendered")}
                  >
                    <Icon name={"redflag"} className="pr-2"></Icon>
                    Apply for Surrender
                  </button>
                )}
              </div>
            )}
        </div>

        {open && popupData && (
          <div className="license-popup-overlay">
            <div className="license-popup">
              <div className="popup-header d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="fw-semibold fs-5 m-0">{popupData.title}</h6>
                  <p>{popupData.description}</p>
                </div>
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={closePopup}
                ></button>
              </div>
              <hr  className="mt-0"/>
              <div className="popup-body fw-semibold fs-6">
                {popupData.confirmation}
              </div>
              <hr />
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  className="button-secondary w-100 fw-semibold"
                  onClick={closePopup}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-primary w-100 fw-semibold"
                  onClick={handleApplicationTypeUpdate}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
        {license && (
          <EditDetails
            isOpen={isEditOpen}
            onClose={closeEditPopup}
            activeSection="license-dashboard"
            initialData={{
              license_name: license.license_name || "",
              description: license.description || "",
              assigned_to_user_id: license.assigned_to_user_id || "",
              watcher_ids: license.watcher_ids || [],
              company_id: license.company_id || license.companyid || "",
              associate_company_id: license.associate_company_id || license.associate_companyid || "",
              location_id: license.location_id || "",
            }}
            onSave={handleSave}
            isLoading={updating}
            selectState={states || []}
            selectCity={cities || []}
          />
        )}
        {isLoading && <PageLoader />}
      </div>
    </>
  );
};

export default LicenseDetail;
